import { Heart, Plus } from "@phosphor-icons/react";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import Price from "components/common/Price";
import ProductCardImage from "components/upsell/cards/ProductCardImage";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import { useWishlistContext } from "lib/contexts/WishlistProvider";
import { handleWishlistAddItem, handleWishlistRemoveItem } from "lib/utils/wishlist-v2";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { useLocale } from "lib/hooks/useLocale";
import { useUpsellValues } from "lib/hooks/useUpsellValues";
import { UpSellProductCardProps, UpSellVariantType } from "lib/types/upsell";
import { analyticsEvents, eventTypes } from "lib/utils/constants";
import { publishPostMessage, shouldRedirectToParent, truncateString } from "lib/utils/helpers";
import { errorToast, infoToast, successToast } from "lib/utils/toasters";
import React, { useMemo, useState } from "react";
import UpsellBottomsheet from "../bottomsheet/UpsellBottomsheet";
import UpSellVariantSelectorDialog from "../dialogs/UpSellVariantsSelectorDialog";
import { Icon } from "@shopflo/ui/icon";
import { useToken } from "lib/contexts/TokenProvider";
import { getTextColorByBrightness, getStyleFromCSSVariable } from "@shopflo/ui/utils";
import DotsLoader from "components/common/loaders/DotsLoader";

const UpSellProductCard: React.FC<UpSellProductCardProps> = (props) => {
  const {
    isDiscounted,
    product,
    keyIdentifier,
    position,
    showPreview,
    isLast = false,
    customisation,
    tabTitle
  } = props;
  const { t } = useLocale();

  const {
    state: { checkoutId },
    actions: { setCartDialog },
  } = useCheckoutContext();
  const {
    state: { isAuthenticated, isLoggedInOnStore },
  } = useAuthContext();
  const {
    state: { merchant },
  } = useMerchantContext();
  const {
    state: { user },
  } = useUserContext();

  const wishlistConfig = merchant?.wishlistConfig;
  const {
    state: { wishlistedItems },
    actions: { isWishlisted, refreshWishlist },
  } = useWishlistContext();
  const {
    state: { tokenId },
  } = useToken();

  const { sendAnalyticsEvent } = useSendAnalyticsEvent();

  const [bottsomSheetState, setBottomSheetState] = useState({
    preview: false,
    variant: false,
  });
  const [selectedVariant, setSelectedVariant] = useState<UpSellVariantType | null>(null);

  const availableVariants = useMemo(
    () => product?.variants?.filter((variant) => !Boolean(variant?.outOfStock)),
    [product],
  );

  const variant = product?.variants?.[0];
  const variantData = variant
    ? {
        originalPrice: variant?.originalPrice,
        currentPrice: variant?.currentPrice,
        discountedPrice: variant?.discountedPrice,
      }
    : null;

  const { percentageDiscount, getCompareAtPrice, isLoading, handleAddBtnClick } = useUpsellValues({
    upsellProduct: props,
  });

  const getTotalPrice = useMemo(() => {
    if (isDiscounted && variantData?.discountedPrice) {
      return variantData?.discountedPrice;
    }
    return variantData?.currentPrice ?? 0;
  }, [isDiscounted, variantData]);

  // Cart surfaces only. Checkout can run as a standalone page rather than
  // inside the cart iframe, so wishlist is not offered there.
  //
  // Both sessions must hold: isAuthenticated is this iframe's, isLoggedInOnStore
  // is the store's. Undefined means the storefront script has not answered yet,
  // so it stays hidden until it does rather than flashing in and out.
  const isWishlistEnabled = useMemo(() => {
    if (position === "PAYMENT_PAGE") return false;
    if (!isAuthenticated || !Boolean(isLoggedInOnStore)) return false;
    if (!Boolean(wishlistConfig?.isEnabled)) return false;
    if (position === "CART" && !Boolean(wishlistConfig?.config?.cart?.enabled)) return false;
    return true;
  }, [position, isAuthenticated, isLoggedInOnStore, wishlistConfig]);

  const isItemInWishlist = useMemo(() => {
    return isWishlisted(product?.variants?.[0]?.variantId);
  }, [wishlistedItems, product?.variants]);

  const wishlistIconFillStyle = getStyleFromCSSVariable("--flo-primary-button-color", "fill");

  const handleAddToWishlist = async () => {
    if (!isAuthenticated && position === "CART") {
      if (shouldRedirectToParent(merchant?.disableThirdPartyCookieRedirect)) {
        let redirectUrl = new URL(document.location.href);
        let checkoutUrlSearchParams = new URLSearchParams(redirectUrl.search);
        checkoutUrlSearchParams.delete("page");
        checkoutUrlSearchParams.append("checkoutId", checkoutId);
        checkoutUrlSearchParams.append("tokenId", tokenId ?? "");
        redirectUrl.search = checkoutUrlSearchParams.toString();
        publishPostMessage(eventTypes.PARENT_REDIRECT, {
          redirectUrl: redirectUrl.href,
          clearCart: false,
        });
        return;
      }
      setCartDialog("cartAuthentication");
      return;
    }
    if (!isAuthenticated) {
      infoToast(t("login_to_add_to_wishlist"), 3000);
      return;
    }
    try {
      const success = await handleWishlistAddItem(
        merchant?.merchantId ?? "",
        user?.uid ?? "",
        { productId: product?.productId, variantId: product?.variants?.[0]?.variantId },
        "CART",
      );
      if (success) {
        successToast(t("added_to_wishlist"), 3000);
        sendAnalyticsEvent({
          eventName: analyticsEvents.ADDED_TO_WISHLIST,
          eventType: "flo_action",
          metaData: {
            wishlistData: {
              variantId: product?.variants?.[0]?.variantId,
              variantName: product?.variants?.[0]?.name,
              productId: product?.productId,
              productName: product?.name,
              parent: "UPSELL",
            },
          },
        });
      } else {
        errorToast(t("error_adding_to_wishlist"), 3000);
      }
    } finally {
      refreshWishlist();
    }
  };

  const handleRemoveFromWishlist = async () => {
    try {
      const success = await handleWishlistRemoveItem(
        merchant?.merchantId ?? "",
        user?.uid ?? "",
        { productId: product?.productId, variantId: product?.variants?.[0]?.variantId },
        "CART",
      );
      if (success) {
        successToast(t("removed_from_wishlist"), 3000);
        sendAnalyticsEvent({
          eventName: analyticsEvents.REMOVED_FROM_WISHLIST,
          eventType: "flo_action",
          metaData: {
            wishlistData: {
              variantId: product?.variants?.[0]?.variantId,
              variantName: product?.variants?.[0]?.name,
              productId: product?.productId,
              productName: product?.name,
              parent: "UPSELL",
            },
          },
        });
      } else {
        errorToast(t("error_removing_from_wishlist"), 3000);
      }
    } finally {
      refreshWishlist();
    }
  };

  const triggerUpsellPreviewEvent = (eventName: string) => {
    sendAnalyticsEvent({
      eventName: eventName,
      eventType: "click",
      metaData: {
        upsellData: {
          position: position,
          productId: product.productId,
          UpSellSection: "generic",
          groupIds: product.upsellIds,
          variantId: variant?.variantId,
        },
      },
    });
  };

  return (
    <>
      <div
        onClick={() => {
          if (showPreview) {
            setBottomSheetState((prev) => ({ ...prev, preview: true }));
            triggerUpsellPreviewEvent(analyticsEvents.UPSELL_PREVIEW_CLICKED);
          }
        }}
        key={keyIdentifier}
        className="upsell-card relative flex h-fit w-[196px] snap-start flex-col gap-2 cursor-pointer shrink-0">
        {!isLast && (
          <div
            className="absolute top-0 bottom-0 right-[-16px] z-[1] w-[1px] opacity-40"
            style={{
              backgroundColor: getTextColorByBrightness({
                bgHexColor: customisation?.backgroundColor ?? "#FFFFFF",
                lightOpacity: 24,
              }),
            }}
          />
        )}
        <div className="flex flex-row gap-3 flex-1">
          <ProductCardImage
            imageLink={product?.variants?.[0]?.imageLink}
            backgroundColor={customisation?.backgroundColor}>
            {Math.round(percentageDiscount(variant, isDiscounted)) > 0 && (
              <div
                className="jusitfy-center absolute -left-[3px] -top-[3px] flex flex-col rounded-br-xl rounded-tl-xl border border-carbon-lighter bg-yay-dark px-2 py-1 text-xxs font-semibold text-white"
                style={{
                  backgroundColor: customisation?.offerBadgeColor,
                  borderColor: getTextColorByBrightness({
                    bgHexColor: customisation?.offerBadgeColor ?? "#FFFFFF",
                    lightOpacity: 24,
                  }),
                }}>
                <span>{Math.round(percentageDiscount(variant, isDiscounted))}%</span>
                <span>OFF</span>
              </div>
            )}
          </ProductCardImage>
          <div className="flex-1 flex flex-col space-y-0">
            <p
              className="mt-1 text-wrap line-clamp-2 text-text-xs-medium text-coal-dark"
              style={{
                color: customisation?.textColor,
              }}>
              {truncateString(product?.name, 32)}
            </p>
            {Boolean(availableVariants?.length) && (
              <button
                className="flex flex-row space-x-0.5 text-xxs font-medium text-dark"
                onClick={(e) => {
                  if (Boolean(product?.variants?.length > 1)) {
                    e?.stopPropagation();
                    handleAddBtnClick(Boolean(product?.variants?.length > 1));
                  }
                }}>
                <Price
                  total={getTotalPrice}
                  compareAt={getCompareAtPrice(variant, isDiscounted)}
                  orientation="horizontal"
                  customization={{
                    textColor: customisation?.textColor,
                  }}
                />
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-row w-full gap-2">
          {isWishlistEnabled && !isDiscounted && (
            <div
              className="w-8 h-8 border-1 rounded-md border flex justify-center items-center"
              onClick={(e) => {
                e?.stopPropagation();
                isItemInWishlist ? handleRemoveFromWishlist() : handleAddToWishlist();
              }}
              style={{
                borderColor: getTextColorByBrightness({
                  bgHexColor: customisation?.backgroundColor ?? "#FFFFFF",
                  lightOpacity: 24,
                }),
              }}>
              <Icon
                IconComponent={Heart}
                size="md"
                iconProps={{
                  className: `h-4 w-4 ${isItemInWishlist ? "fill-primary-dark" : ""}`,
                  strokeWidth: 2.5,
                  weight: isItemInWishlist ? "fill" : "regular",
                  color: getTextColorByBrightness({
                    bgHexColor: customisation?.backgroundColor ?? "#FFFFFF",
                    lightOpacity: 60,
                    darkOpacity: 60,
                  }),
                  style: isItemInWishlist ? wishlistIconFillStyle : undefined,
                }}
              />
            </div>
          )}
          <button
            onClick={(e) => {
              e?.stopPropagation();
              handleAddBtnClick(Boolean(product?.variants?.length > 1));
            }}
            className="flex flex-1 h-8 items-center justify-center space-x-1 rounded-lg bg-transparent border-1 border-yay-dark px-3 text-sm font-medium text-yay-dark"
            style={{
              borderColor: customisation?.buttonColor,
              color: customisation?.buttonColor,
            }}>
            {isLoading ? (
              <DotsLoader color={customisation?.buttonColor ?? "var(--flo-yay-color)"} />
            ) : (
              <>
                <Plus size={16} weight="bold" />
                <span className="text-text-xs-semibold">{t("add").toUpperCase()}</span>
              </>
            )}
          </button>
        </div>
      </div>
      {isLoading && <OverlaySpinner />}
      <UpsellBottomsheet
        selectedVariant={selectedVariant}
        open={bottsomSheetState.preview}
        onClose={() => setBottomSheetState((prev) => ({ ...prev, preview: false }))}
        upsellProduct={props}
        position={position}
        handleVariantChange={() => {
          setBottomSheetState({
            variant: true,
            preview: false,
          });
        }}
      />
      <UpSellVariantSelectorDialog
        upsellProduct={props.product}
        isVariantSelectorOpen={bottsomSheetState.variant}
        closeVariantSelector={() => setBottomSheetState((prev) => ({ ...prev, variant: false }))}
        position={position}
        tabTitle={tabTitle}
        onPreviousClick={() => {
          setBottomSheetState({
            variant: false,
            preview: true,
          });
        }}
        onVariantSelected={(varinatId) => {
          const variant = product?.variants?.find((variant) => variant.variantId === varinatId);
          setSelectedVariant(variant ?? null);
          setBottomSheetState({ variant: false, preview: true });
        }}
        enbaleAddActions={false}
      />
    </>
  );
};

export default UpSellProductCard;
